import {useEffect, useRef, useState} from "react";
import {useLanguage} from "../../hooks/LanguageContext";

const DigitInput = ({
                        length,
                        label,
                        name,
                        onChange,
                    }) => {
    const [digits, setDigits] = useState(Array(length).fill(""));
    const inputsRef = useRef([]);
    const {direction} = useLanguage();

    useEffect(() => {
        // Ensure correct input direction (CSS)
        inputsRef.current.forEach((input) => {
            if (input) input.dir = direction;
        });
    }, [direction]);

    const handleChange = (index, event) => {
        const value = event.target.value.replace(/\D/, ""); // Allow only numbers
        if (value.length > 1) return;

        const newDigits = [...digits];
        newDigits[index] = value;
        setDigits(newDigits);

        // Determine next input direction based on LTR/RTL
        const nextIndex = direction === "rtl" ? index + 1 : index - 1;

        // Move focus to the next input if a digit is entered
        if (value && nextIndex >= 0 && nextIndex < length) {
            inputsRef.current[nextIndex]?.focus();
        }

        // Trigger parent onChange with an event-like object
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: newDigits.join(""),
                },
            });
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace") {
            event.preventDefault();
            const newDigits = [...digits];
            newDigits[index] = ""; // Clear current input
            setDigits(newDigits);

            // Determine previous input direction based on LTR/RTL
            const prevIndex = direction === "rtl" ? index - 1 : index + 1;

            // Move focus back to the previous input if not at the start
            if (prevIndex >= 0 && prevIndex < length) {
                inputsRef.current[prevIndex]?.focus();
            }

            // Trigger onChange update
            if (onChange) {
                onChange({
                    target: {
                        name,
                        value: newDigits.join(""),
                    },
                });
            }
        }
    };

    const handleClick = () => {
        // If all inputs are empty, focus the first input
        if (digits.every((digit) => digit === "")) {
            const firstIndex = direction === "rtl" ? 0 : length -1;
            inputsRef.current[firstIndex]?.focus();
        }
    };

    return (
        <div className="space-y-2" dir={direction}>
            <div className="flex items-center gap-2">
                <span className="text-primary-700 md:text-xl !font-bold whitespace-nowrap">{label}</span>
            </div>
            <div className={`flex gap-1 flex-row-reverse justify-end`} onClick={handleClick}>
                {digits.map((digit, index) => (
                    <input
                        key={index}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        ref={(el) => (inputsRef.current[index] = el)}
                        className="w-8 h-8 md:w-14 md:h-14 text-center border-2 border-primary-700 bg-bg-light
                            md:text-xl font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                ))}
            </div>
        </div>
    );
};

export default DigitInput;
